/**
 * Modal — el diálogo del design system.
 *
 * Portado de `clobi-design-system@0.1.0`. **Convive con `components/Modal.tsx`**
 * mientras se migran sus 25 consumidores: la API es casi la misma y la única
 * diferencia que rompe es `footer` → `actions`.
 *
 * ─── Lo que gana sobre el del repo ────────────────────────────────────────────
 *
 * El `Modal` actual tiene **cero atributos ARIA** y solo escucha Escape. Este
 * trae `role="dialog"`, `aria-modal` y `aria-labelledby`, y por `useDismissable`
 * suma la trampa de foco, el bloqueo del scroll de fondo y la restauración del
 * foco al cerrar.
 *
 * ⚠️ `dismissOnScrimClick={false}` es para confirmaciones destructivas: exige
 * apretar un botón en vez de cerrarse con un clic afuera, que es fácil de hacer
 * sin querer y deja la duda de si la acción ocurrió.
 *
 * ─── Dos cosas donde este Modal SE APARTA del sistema ─────────────────────────
 *
 * 1. **Cinco tamaños y no tres.** Medido sobre los 34 modales del producto, 8 no
 *    entran en los 760px del `lg`. Ver el comentario de `.cl-modal--xl` en
 *    `ds-glass.css`.
 *
 * 2. **Tiene botón de cerrar.** El del sistema no lo trae ni ofrece dónde
 *    ponerlo —el `Drawer` al menos expone un slot en su header—, y los 34
 *    consumidores del repo lo tienen hoy. Sin él, un modal con
 *    `dismissOnScrimClick={false}` y sin acciones queda sin salida con el mouse.
 *    El control es el `IconButton` del repo, que exige `aria-label` en tiempo de
 *    compilación: es el mismo criterio del `DateField` —la capa del sistema, el
 *    control del repo— en vez de escribir un botón suelto.
 *
 * 🔴 Los dos apartes hay que contárselos a Sebastián. Si no, la próxima entrega
 * del sistema los borra, que es literalmente lo que pasó con el `DateField`.
 */
import { useId } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties, ReactNode } from 'react'
import { X } from 'lucide-react'
import { IconButton } from '../IconButton'
import { GlassPanel } from './GlassPanel'
import { useDismissable } from './useDismissable'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const CLASE_POR_TAMANO: Record<ModalSize, string> = {
  sm: 'cl-modal--sm',
  md: 'cl-modal--md',
  lg: 'cl-modal--lg',
  xl: 'cl-modal--xl',
  '2xl': 'cl-modal--2xl',
}

export interface ModalProps {
  open?: boolean
  onClose?: () => void
  title?: ReactNode
  subtitle?: ReactNode
  /** Pie de acciones. El botón primario va a la derecha. */
  actions?: ReactNode
  /** 420 / 560 / 760 / 900 / 1024 px de ancho máximo. */
  size?: ModalSize
  /** Confirmaciones destructivas: exige el botón, no el clic afuera. */
  dismissOnScrimClick?: boolean
  /** En ≤640px colapsa a hoja pegada al borde inferior. */
  sheetOnMobile?: boolean
  /**
   * Alto FIJO y cuerpo sin scroll propio: el cuerpo pasa a ser un contenedor
   * flex en columna, sin padding, y el scroll lo maneja cada hijo con
   * `flex-1 min-h-0 overflow-y-auto`.
   *
   * Es para diálogos con pestañas o paneles: la barra de pestañas queda fija y
   * el modal mide lo mismo en todas, en vez de crecer o encogerse según lo que
   * muestre cada una. Sin esto, el cuerpo del componente es un bloque con
   * scroll y la barra se va con el contenido.
   */
  fill?: boolean
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

export function Modal({
  open = false,
  onClose,
  title,
  subtitle,
  actions,
  size = 'md',
  dismissOnScrimClick = true,
  sheetOnMobile = true,
  fill = false,
  children,
  className = '',
  style,
}: ModalProps) {
  const idTitulo = useId()
  const contenedor = useDismissable({ open, onClose, modal: true })

  if (!open) return null

  /*
    🔴 Va por PORTAL a `document.body`, y no es cosmético.

    Un ancestro con `backdrop-filter` —el `bg-glass backdrop-blur-glass` que
    envuelve casi todos los paneles del producto— hace DOS cosas a la vez con un
    descendiente `position: fixed`: se vuelve su bloque contenedor, así que el
    `inset: 0` deja de referirse a la ventana; y crea un contexto de
    apilamiento, así que el `z-index` del modal ya no compite con el header ni
    con las pestañas, por alto que sea. Si además el panel trae
    `overflow: hidden`, lo recorta.

    Lo reportó Gabriel con una captura del panel de plataforma: el diálogo salía
    corrido, cortado por arriba y DEBAJO de las pestañas. No hay `z-index` que
    lo arregle desde adentro — la única salida es no estar adentro.

    ⚠️ El scrim se monta en `body` pero el modal sigue siendo hijo de este
    componente en el árbol de React, así que los eventos, el contexto y el foco
    se comportan igual: lo único que cambia es dónde vive en el DOM.
  */
  return createPortal(
    <div
      className="cl-scrim cl-scrim--modal"
      style={{ padding: 'var(--space-4)' }}
      data-sheet={sheetOnMobile ? 'true' : undefined}
      onClick={dismissOnScrimClick ? onClose : undefined}
    >
      <GlassPanel
        as="div"
        level="solid"
        padded={false}
        ref={contenedor}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? idTitulo : undefined}
        className={`cl-modal ${CLASE_POR_TAMANO[size] ?? CLASE_POR_TAMANO.md} ${fill ? 'cl-modal--fill' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
        data-sheet={sheetOnMobile ? 'true' : undefined}
        style={style}
        // Sin esto, un clic dentro del diálogo burbujea hasta el velo y lo
        // cierra — incluido el clic que empieza a seleccionar texto.
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* El header aparece también sin título cuando hay con qué cerrar: la
            alternativa es un diálogo sin salida visible. */}
        {(title || subtitle || onClose) && (
          <div className="cl-modal__header">
            <div style={{ minWidth: 0 }}>
              {title && (
                <div
                  id={idTitulo}
                  style={{
                    fontSize: 'var(--type-card-title-size)',
                    fontWeight: 'var(--type-card-title-weight)' as CSSProperties['fontWeight'],
                  }}
                >
                  {title}
                </div>
              )}
              {subtitle && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: 'rgb(var(--color-content-subtle))',
                    marginTop: 3,
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
            {onClose && (
              <IconButton
                onClick={onClose}
                aria-label="Cerrar"
                icon={<X size={16} />}
                className="-mr-1 -mt-0.5 shrink-0"
              />
            )}
          </div>
        )}
        <div className="cl-modal__body">{children}</div>
        {actions && <div className="cl-modal__footer">{actions}</div>}
      </GlassPanel>
    </div>,
    document.body,
  )
}
